<script setup lang="ts">
import { ActionIcon, MenuItem, MenuPopover, MenuSwitchItem } from '@taler/ui'
import { formatInstant, formatInstantDate, formatInstantTimeUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuth } from '@/app/auth/use-auth'
import { useAccountingView } from '@/app/accounting-view'
import { readBuildInfo } from '@/app/config'
import { useSession } from '@/app/session'
import { canManageMembership } from '@/features/membership'
import { canManageIdentity } from '@/features/identity-admin'
import { canRestoreBackup } from '@/features/exports'
import { canManageLedgerLifecycle } from '@/features/ledger-lifecycle'

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const session = useSession()
const auth = useAuth()
const router = useRouter()
const { accountingView, setAccountingView } = useAccountingView()

const canManage = computed(() => canManageMembership(session.can))
const canManageAdminIdentity = computed(() => canManageIdentity(session.principal.value?.authorities ?? []))
/** Membership itself is the gate - no dedicated `Capability`, no dedicated capability exists. */
const canManageNotificationPreferences = computed(() => session.activeLedger.value !== undefined)
const canViewBackup = computed(() => canRestoreBackup(session.principal.value !== undefined))
const canManageLifecycle = computed(() => canManageLedgerLifecycle(session.can))

const build = readBuildInfo()
const buildCommit = build.commitSha.slice(0, 7)
const buildTime = build.buildTime
const buildTimeIso = buildTime && formatInstant(buildTime)
const buildTimeLabel = computed(() =>
  buildTime ? `${formatInstantDate(buildTime, currentLocale.value)} ${formatInstantTimeUtc(buildTime)}` : '',
)

const initials = computed(() => {
  const subject = session.principal.value?.subject ?? ''
  const local = subject.split('@')[0] ?? ''
  const parts = local.split(/[._-]/).filter((part) => part !== '')
  const chars = parts.length >= 2 ? [parts[0]![0], parts[1]![0]] : [local[0], local[1]]
  return (
    chars
      .filter((c): c is string => c !== undefined)
      .join('')
      .toUpperCase() || '?'
  )
})

async function signOut(): Promise<void> {
  await auth.signOut()
  await router.push({ name: 'sign-in' })
}
</script>

<template>
  <MenuPopover>
    <template #trigger>
      <button
        type="button"
        :aria-label="t('shell.account')"
        class="flex h-[var(--density-control)] w-[var(--density-control)] items-center justify-center rounded-full bg-[color:var(--color-text)] text-[length:var(--text-step-2)] font-semibold text-[color:var(--color-text-inverse)]"
      >
        {{ initials }}
      </button>
    </template>

    <p
      v-if="session.principal.value"
      class="truncate px-[var(--space-cell)] py-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
      :style="{ fontFamily: 'var(--font-mono)' }"
    >
      {{ session.principal.value.subject }}
    </p>
    <MenuItem v-if="canManage" as-child>
      <RouterLink :to="{ name: 'members' }">
        <ActionIcon name="members" />
        {{ t('membership.title') }}
      </RouterLink>
    </MenuItem>
    <MenuItem v-if="canManageNotificationPreferences" as-child>
      <RouterLink :to="{ name: 'notification-preferences' }">
        <ActionIcon name="bell" />
        {{ t('notificationPreferences.title') }}
      </RouterLink>
    </MenuItem>
    <MenuItem v-if="canManageAdminIdentity" as-child>
      <RouterLink :to="{ name: 'admin-identity' }">
        <ActionIcon name="members" />
        {{ t('identityAdmin.title') }}
      </RouterLink>
    </MenuItem>
    <MenuItem v-if="canViewBackup" as-child>
      <RouterLink :to="{ name: 'backup-restore' }">
        <ActionIcon name="backup" />
        {{ t('backupRestore.title') }}
      </RouterLink>
    </MenuItem>
    <MenuItem v-if="canManageLifecycle" as-child>
      <RouterLink :to="{ name: 'ledger-lifecycle' }">
        <ActionIcon name="advanced" />
        {{ t('ledgerLifecycle.title') }}
      </RouterLink>
    </MenuItem>
    <div role="separator" class="my-[var(--space-hair)] h-px bg-[color:var(--color-border)]" />
    <MenuSwitchItem
      :model-value="accountingView"
      :label="t('shell.accountingView')"
      :description="t('shell.accountingViewHint')"
      @update:model-value="setAccountingView"
    >
      <template #icon><ActionIcon name="advanced" /></template>
    </MenuSwitchItem>
    <div role="separator" class="my-[var(--space-hair)] h-px bg-[color:var(--color-border)]" />
    <MenuItem @select="signOut">
      <ActionIcon name="signOut" />
      {{ t('auth.signOut') }}
    </MenuItem>
    <div role="separator" class="my-[var(--space-hair)] h-px bg-[color:var(--color-border)]" />
    <p
      :title="build.commitSha"
      class="px-[var(--space-cell)] py-[var(--space-inline)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
    >
      {{ t('shell.version') }}
      <span :style="{ fontFamily: 'var(--font-mono)' }">{{ buildCommit }}</span>
      <time v-if="buildTime" :datetime="buildTimeIso" class="ml-[var(--space-inline)]">{{
        buildTimeLabel
      }}</time>
    </p>
  </MenuPopover>
</template>
